import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const HUNT_TIMEOUT_MS = 180000;

const pollHuntJob = async (jobId, onProgress) => {
  const started = Date.now();
  while (Date.now() - started < HUNT_TIMEOUT_MS) {
    await new Promise((r) => setTimeout(r, 2000));
    const { data: job } = await axios.get(`${API}/agent/hunt/job/${jobId}`);
    onProgress({ stage: job.stage, progress: job.progress });
    if (job.status === "done") return job.result;
    if (job.status === "failed") throw new Error(job.error || "Hunt failed");
  }
  throw new Error("Hunt timed out — please run it again");
};

const leadPayload = (userId, company, source) => ({
  userId,
  companyName: company.companyName || "Not disclosed on profile",
  contactName: company.personName || null,
  contactTitle: company.personTitle || null,
  linkedinUrl: company.url?.includes("linkedin.com") ? company.url : null,
  contactEmail: company.email || null,
  email: company.email || null,
  website: company.companyDomain ? `https://${company.companyDomain}` : null,
  industry: company.companyIndustry || null,
  companySize: company.employeeCount ? `${Number(company.employeeCount).toLocaleString()} employees` : null,
  phone: company.phone || null,
  source,
  sourceUrl: company.url || company.registry?.url || null,
  notes: [...(company.companyFacts || []).slice(0, 2), ...(company.whyGoodFit || []).slice(0, 3)].join("; ") || company.snippet || null,
  icpMatch: company.icpMatch?.score || null,
  verification: company.verification || null,
  registry: company.registry || null,
  evidence: company.evidence || [],
});

const dismissSession = async (sessionId) => {
  if (!sessionId || String(sessionId).startsWith("hunt-")) return;
  try { await axios.post(`${API}/hunt/dismiss-all/${sessionId}`); } catch (e) { console.warn("Could not dismiss hunt session:", e?.message || e); }
};

// Hunt/Farm runs, the current result set, and per-lead disposition (save / dismiss) with persistence gating.
export const useProspectingSession = (userId, onRefresh) => {
  const [huntProcessing, setHuntProcessing] = useState(false);
  const [huntJob, setHuntJob] = useState(null);
  const [farmProcessing, setFarmProcessing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [results, setResults] = useState(null);
  const [savedLeads, setSavedLeads] = useState(new Set());
  const [dismissedLeads, setDismissedLeads] = useState(new Set());
  const [dispositionOrder, setDispositionOrder] = useState([]);   // chronological log for the results footer
  const [savingLead, setSavingLead] = useState(null);
  const [huntSearchMode, setHuntSearchMode] = useState("auto");
  const [farmSearchMode, setFarmSearchMode] = useState("auto");
  const [huntPrompt, setHuntPrompt] = useState("");
  const [farmPrompt, setFarmPrompt] = useState("");
  const [persistedHuntLeads, setPersistedHuntLeads] = useState(null);
  const [persistedFarmLeads, setPersistedFarmLeads] = useState(null);

  const undispositionedCount = useCallback((leads) => {
    if (!leads?.companiesFound) return 0;
    return leads.companiesFound.filter((_, idx) => !savedLeads.has(idx) && !dismissedLeads.has(idx)).length;
  }, [savedLeads, dismissedLeads]);

  const huntPending = persistedHuntLeads ? undispositionedCount(persistedHuntLeads) : 0;
  const farmPending = persistedFarmLeads ? undispositionedCount(persistedFarmLeads) : 0;
  const canRunNewHunt = huntPending === 0;
  const canRunNewFarm = farmPending === 0;

  useEffect(() => {
    if (!userId) return;
    const loadPersistedLeads = async () => {
      try {
        const response = await axios.get(`${API}/hunt/history/${userId}?limit=1&active=true`);
        const session = response.data?.sessions?.[0];
        if (!session) return;
        const hasUndispositioned = session.companiesFound?.some((_, idx) => !session.savedIndices?.includes(idx) && !session.dismissedIndices?.includes(idx));
        if (!hasUndispositioned) return;
        const sessionData = {
          mode: session.mode || "Hunt",
          companiesFound: session.companiesFound || [],
          insights: session.aiInsights,
          totalResults: session.totalResults,
          huntSessionId: session.huntSessionId,
          data: { targeting: session.targeting, searchStats: session.searchStats, guidance: session.guidance, needsGuidance: !!session.guidance },
          fromHistory: true,
        };
        (session.mode?.toLowerCase() === "farm" ? setPersistedFarmLeads : setPersistedHuntLeads)(sessionData);
        if (session.savedIndices) setSavedLeads(new Set(session.savedIndices));
        if (session.dismissedIndices) setDismissedLeads(new Set(session.dismissedIndices));
        setDispositionOrder([...(session.savedIndices || []), ...(session.dismissedIndices || [])]);
        setResults(sessionData);
        setShowResults(false);
        setShowResultsModal(false);
      } catch (error) {
        console.error("Error loading persisted hunt session:", error);
      }
    };
    loadPersistedLeads();
  }, [userId]);

  const beginRun = () => {
    setShowResults(false);
    setSavedLeads(new Set());
    setDismissedLeads(new Set());
    setDispositionOrder([]);
  };

  const runHunt = async () => {
    setHuntProcessing(true);
    setHuntJob({ stage: "Starting hunt", progress: 1 });
    beginRun();
    try {
      const params = huntSearchMode === "prompt" && huntPrompt.trim() ? { prompt: huntPrompt.trim() } : {};
      const response = await axios.post(`${API}/agent/hunt/${userId}`, null, { params });
      const data = response.data?.async && response.data?.jobId ? await pollHuntJob(response.data.jobId, setHuntJob) : response.data;
      const companiesFound = data.companiesFound || [];
      const huntData = { mode: "Hunt", data, companiesFound, huntSessionId: data.huntSessionId || `hunt-${Date.now()}` };
      setResults(huntData);
      setPersistedHuntLeads(huntData);
      setShowResults(true);
      setShowResultsModal(true);
      const count = companiesFound.length;
      const down = Object.values(data.searchStats?.sourceStatus || {}).filter((s) => s && !s.ok && s.errors?.length).map((s) => s.label);
      if (count === 0 && down.length) toast.warning(`Hunt found nothing — ${down.join(", ")} unavailable. Check Settings → Data sources.`);
      else if (data.needsGuidance) toast.message(`Hunt finished with ${count} strong prospect${count === 1 ? "" : "s"} — I need your direction on targeting`);
      else toast.success(`Hunt Mode: ${count} verified prospect${count === 1 ? "" : "s"} found`);
      await onRefresh();
    } catch (error) {
      console.error("Error:", error);
      toast.error(error?.message || "Hunt mode failed");
    } finally {
      setHuntProcessing(false);
      setHuntJob(null);
    }
  };

  const runFarm = async () => {
    setFarmProcessing(true);
    beginRun();
    try {
      const params = farmSearchMode === "prompt" && farmPrompt.trim() ? { prompt: farmPrompt.trim() } : {};
      const response = await axios.post(`${API}/agent/farm/${userId}`, null, { params });
      if (response.data.needsAccountData) {
        toast.info(response.data.message);
        setResults({ mode: "Farm", needsAccounts: true, message: response.data.insights });
        setShowResults(true);
      } else {
        const farmData = { mode: "Farm", data: response.data, companiesFound: response.data.companiesFound || [], farmSessionId: response.data.farmSessionId || `farm-${Date.now()}` };
        setResults(farmData);
        setPersistedFarmLeads(farmData);
        setShowResults(true);
        if (farmData.companiesFound.length > 0) setShowResultsModal(true);
        toast.success("Farm Mode: Expansion opportunities identified!");
      }
      await onRefresh();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Farm mode failed");
    } finally {
      setFarmProcessing(false);
    }
  };

  // Persist the decision on the hunt session (so the queue stays cleared after a reload) and log it as a CRM activity.
  const recordDisposition = async (leadIndex, outcome, leadId = null) => {
    const sessionId = results?.huntSessionId;
    if (!sessionId || sessionId.startsWith("hunt-") || results?.mode?.toLowerCase() !== "hunt") return true;
    try {
      await axios.post(`${API}/prospecting/disposition/${userId}`, { huntSessionId: sessionId, index: leadIndex, outcome, leadId });
      return true;
    } catch (e) {
      toast.error(`Could not record the ${outcome === "saved" ? "save" : "dismissal"} — it may reappear after a reload`);
      return false;
    }
  };

  // Completion is derived from state (never from a stale closure): once every lead is saved or dismissed, the queue clears.
  const completedRef = useRef(null);
  useEffect(() => {
    const total = results?.companiesFound?.length || 0;
    const sessionKey = results?.huntSessionId || results?.farmSessionId;
    if (!total || !sessionKey || savedLeads.size + dismissedLeads.size < total || completedRef.current === sessionKey) return;
    completedRef.current = sessionKey;
    const mode = results.mode?.toLowerCase();
    if (mode === "hunt") setPersistedHuntLeads(null);
    else if (mode === "farm") setPersistedFarmLeads(null);
    toast.success(`All ${total} leads processed — ${savedLeads.size} saved, ${dismissedLeads.size} dismissed. You can hunt again.`);
  }, [results, savedLeads, dismissedLeads]);

  const saveLead = async (company, leadIndex) => {
    setSavingLead(leadIndex);
    try {
      const leadData = leadPayload(userId, company, results?.mode?.toLowerCase() || "hunt");
      const { data: saved } = await axios.post(`${API}/leads`, leadData);
      setSavedLeads((prev) => new Set([...prev, leadIndex]));
      setDispositionOrder((prev) => [...prev, leadIndex]);
      toast.success(saved?.duplicate ? `"${leadData.contactName || leadData.companyName}" was already in your CRM — linked` : `Lead "${leadData.contactName || leadData.companyName}" saved to CRM`);
      await recordDisposition(leadIndex, "saved", saved?.leadId || null);
      onRefresh?.();
    } catch (error) {
      console.error("Error saving lead:", error);
      toast.error("Failed to save lead");
    } finally {
      setSavingLead(null);
    }
  };

  const dismissLead = async (_company, leadIndex) => {
    setDismissedLeads((prev) => new Set([...prev, leadIndex]));
    setDispositionOrder((prev) => [...prev, leadIndex]);
    toast.info("Lead dismissed");
    await recordDisposition(leadIndex, "dismissed");
  };

  const viewPending = () => {
    const pending = !canRunNewHunt ? persistedHuntLeads : !canRunNewFarm ? persistedFarmLeads : null;
    if (!pending) return;
    setResults(pending);
    setShowResults(true);
    setShowResultsModal(true);
  };

  const dismissAllPending = async () => {
    await dismissSession(persistedHuntLeads?.huntSessionId);
    await dismissSession(persistedFarmLeads?.huntSessionId);
    setPersistedHuntLeads(null);
    setPersistedFarmLeads(null);
    setSavedLeads(new Set());
    setDismissedLeads(new Set());
    setResults(null);
    setShowResults(false);
    toast.success("All pending leads dismissed — you can hunt again");
  };

  const rerunHunt = async () => {
    await dismissSession(results?.huntSessionId);
    setShowResultsModal(false);
    setPersistedHuntLeads(null);
    setResults(null);
    runHunt();
  };

  return {
    results, showResults, setShowResults, showResultsModal, setShowResultsModal,
    savedLeads, dismissedLeads, dispositionOrder, savingLead,
    hunt: { mode: huntSearchMode, setMode: setHuntSearchMode, prompt: huntPrompt, setPrompt: setHuntPrompt, processing: huntProcessing, job: huntJob,
      pendingCount: huntPending, disabled: huntProcessing || (huntSearchMode === "prompt" && !huntPrompt.trim()) || !canRunNewHunt, onRun: runHunt },
    farm: { mode: farmSearchMode, setMode: setFarmSearchMode, prompt: farmPrompt, setPrompt: setFarmPrompt, processing: farmProcessing,
      pendingCount: farmPending, disabled: farmProcessing || (farmSearchMode === "prompt" && !farmPrompt.trim()) || !canRunNewFarm, onRun: runFarm },
    huntPending, farmPending, canRunNewHunt, canRunNewFarm,
    saveLead, dismissLead, viewPending, dismissAllPending, rerunHunt,
  };
};
